import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { detectLocale, getStoredLocale, readTelegramLanguageCode } from '@/i18n/detect-locale';
import { createTranslator } from '@/i18n/translate';
import { ApiError, api, getAuthToken, setAuthToken } from '@/lib/api';
import { isInsideTelegramWebApp } from '@/lib/telegram-env';
import { useTelegram } from '@/providers/TelegramProvider';
import { useAuthStore } from '@/stores/auth';

function devBypassErrorMessage(): string {
  const locale = detectLocale({
    stored: getStoredLocale(),
    telegramLanguageCode: readTelegramLanguageCode(),
    browserLanguage: navigator.language,
  });
  return createTranslator(locale)('auth.devBypassError');
}

function networkErrorMessage(): string {
  const locale = detectLocale({
    stored: getStoredLocale(),
    telegramLanguageCode: readTelegramLanguageCode(),
    browserLanguage: navigator.language,
  });
  return createTranslator(locale)('auth.networkError');
}

export function useAuthBootstrap(): {
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  isAccessDenied: boolean;
} {
  const { initData, isReady } = useTelegram();
  const { token, setToken, setSession, setAuthReady, logout } = useAuthStore();

  useEffect(() => {
    if (!import.meta.env.DEV && isReady && !isInsideTelegramWebApp()) {
      setAuthToken(null);
      logout();
    }
  }, [isReady, logout]);

  const loginQuery = useQuery({
    queryKey: ['auth', 'login', initData],
    queryFn: async () => {
      if (import.meta.env.DEV && !initData) {
        const devToken = getAuthToken();
        if (devToken) return { token: devToken };
        try {
          await api.auth.me();
          const bypassToken = 'dev-bypass';
          setAuthToken(bypassToken);
          setToken(bypassToken);
          return { token: bypassToken };
        } catch {
          throw new Error(devBypassErrorMessage());
        }
      }
      const result = await api.auth.login(initData);
      setAuthToken(result.token);
      setToken(result.token);
      return result;
    },
    enabled: isReady && (import.meta.env.DEV || (isInsideTelegramWebApp() && initData.length > 0)),
    retry: (failureCount, err) => {
      if (err instanceof ApiError) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 500,
  });

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => api.auth.me(),
    enabled: !!(token ?? loginQuery.data?.token),
  });

  useEffect(() => {
    if (meQuery.data) {
      setSession(meQuery.data);
      return;
    }
    if (meQuery.isError) {
      setAuthReady(false);
    }
  }, [meQuery.data, meQuery.isError, setSession, setAuthReady]);

  const isLoading =
    !isReady ||
    loginQuery.isLoading ||
    loginQuery.isFetching ||
    (!!loginQuery.data && meQuery.isLoading);
  const isAuthenticated = !!meQuery.data;
  const loginSettled = loginQuery.isFetched || loginQuery.isSuccess || loginQuery.isError;
  const rawError = loginSettled ? (loginQuery.error ?? meQuery.error) : null;
  const error = rawError instanceof TypeError ? new Error(networkErrorMessage()) : rawError;
  const isAccessDenied = error instanceof ApiError && error.code === 'WEBAPP_ACCESS_DENIED';

  return { isLoading, isAuthenticated, error, isAccessDenied };
}
